import React, { useState } from 'react';
import type { BrandIntelligenceAd } from '@shared/schema';
import { AdMediaDialog } from '../ad-media-dialog';

function titleFor(ad: BrandIntelligenceAd) {
  return ad.adCreativeLinkTitle || ad.pageName || `Brand ad ${ad.id}`;
}

function DetailField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return <div><h4 style={{ fontSize: 11, fontWeight: 600, color: '#5F6D82', textTransform: 'uppercase', margin: '0 0 4px' }}>{label}</h4><p style={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5, margin: 0 }}>{value}</p></div>;
}

function BrandAdDetails({ ad, onClose }: { ad: BrandIntelligenceAd; onClose: () => void }) {
  const imageUrl = ad.mediaThumbnailUrl || (ad.mediaType !== 'video' ? ad.mediaUrl : null);
  return <AdMediaDialog title={titleFor(ad)} imageUrl={imageUrl} videoUrl={ad.mediaType === 'video' ? ad.mediaUrl : null} onClose={onClose} previewTestId="brand-ad-detail-image">
    <DetailField label="Source" value={ad.source === 'admin_upload' ? 'Uploaded brand media' : 'Facebook Ad Library'} />
    <DetailField label="Advertiser" value={ad.pageName} />
    <DetailField label="Imported headline" value={ad.adCreativeLinkTitle} />
    <DetailField label="Imported ad copy" value={ad.adCreativeBody} />
    <DetailField label="Link description" value={ad.adCreativeLinkDescription} />
    <DetailField label="Call to action" value={ad.ctaType} />
    <DetailField label="Start date" value={ad.adStartDate} />
    {[ad.detectedAdStyle, ad.detectedAngle, ad.detectedHook, ad.keyMessage, ad.offerType, ad.visualStyle, ad.compositionFamily, ad.promptRecipe].some(Boolean) && <>
      <h3 style={{ fontSize: 12, fontWeight: 600, borderTop: '1px solid #DCE3ED', paddingTop: 14, margin: 0 }}>AI analysis</h3>
      <DetailField label="Ad style" value={ad.detectedAdStyle} />
      <DetailField label="Angle" value={ad.detectedAngle} />
      <DetailField label="Detected hook" value={ad.detectedHook} />
      <DetailField label="Key message" value={ad.keyMessage} />
      <DetailField label="Offer" value={ad.offerType} />
      <DetailField label="Visual style" value={ad.visualStyle} />
      <DetailField label="Composition" value={ad.compositionFamily} />
      <DetailField label="Visual recipe" value={ad.promptRecipe} />
    </>}
  </AdMediaDialog>;
}

function BrandAdCard({ ad, onOpen, onDelete }: { ad: BrandIntelligenceAd; onOpen: () => void; onDelete?: () => void }) {
  const [failed, setFailed] = useState(false);
  const imageUrl = ad.mediaThumbnailUrl || (ad.mediaType !== 'video' ? ad.mediaUrl : null);
  return <div style={{ breakInside: 'avoid', marginBottom: 10, border: '1px solid #DCE3ED', borderRadius: 10, overflow: 'hidden', background: '#F7F9FC' }}>
    <button type="button" data-testid={`brand-ad-open-${ad.id}`} aria-label={`View ad details: ${titleFor(ad)}`} onClick={onOpen} style={{ display: 'block', width: '100%', border: 0, padding: 0, cursor: 'pointer', background: 'transparent', color: 'inherit', textAlign: 'left' }}>
      {imageUrl && !failed ? <img src={imageUrl} alt={titleFor(ad)} loading="lazy" onError={() => setFailed(true)} style={{ display: 'block', width: '100%', height: 'auto' }} /> : <p style={{ minHeight: 100, padding: 14, fontSize: 12, color: '#526074' }}>{ad.adCreativeBody || 'Preview unavailable — view ad details'}</p>}
    </button>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 10px', fontSize: 11, color: '#526074' }}>
      <span>{ad.detectedAdStyle || (ad.source === 'admin_upload' ? 'Uploaded media' : 'Source ad')}</span>
      {onDelete && <button type="button" onClick={onDelete} data-testid={`brand-ad-delete-${ad.id}`} aria-label={`Remove source ad: ${titleFor(ad)}`} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#DC2626', padding: '2px 4px' }}>×</button>}
    </div>
  </div>;
}

/** Both source collections share native-aspect thumbnails and the same viewer. */
export function BrandAdGallery({ ads, onDelete }: { ads: BrandIntelligenceAd[]; onDelete?: (id: number) => void }) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = ads.find((ad) => ad.id === selectedId);
  return <>
    <div data-testid="brand-ad-gallery" style={{ columns: '220px 3', columnGap: 10 }}>
      {ads.map((ad) => <BrandAdCard key={ad.id} ad={ad} onOpen={() => setSelectedId(ad.id)} onDelete={onDelete ? () => onDelete(ad.id) : undefined} />)}
    </div>
    {selected && <BrandAdDetails key={selected.id} ad={selected} onClose={() => setSelectedId(null)} />}
  </>;
}
